// Product gallery viewer: one large slide at a time in a horizontal scroll-snap track.
// Thumbnails and prev/next arrows scroll the track; the active thumbnail and the "n / total"
// counter follow whichever slide is in view (including after a touch swipe).
class ProductGalleryElement extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-product-gallery]');
    if (!this.track) return;
    this.slides = Array.from(this.track.querySelectorAll('[data-gallery-slide]'));
    this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
    this.counter = this.querySelector('[data-gallery-counter]');
    this.index = 0;
    this.controller = new AbortController();
    const { signal } = this.controller;

    for (const thumb of this.thumbs) {
      thumb.addEventListener('click', () => this.goTo(Number(thumb.dataset.galleryThumb)), { signal });
    }
    this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.goTo(this.index - 1), { signal });
    this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.goTo(this.index + 1), { signal });
    this.track.addEventListener('scroll', this.onScroll, { signal, passive: true });
  }

  disconnectedCallback() {
    this.controller?.abort();
    cancelAnimationFrame(this.frame);
  }

  goTo(index) {
    if (!this.slides.length) return;
    const count = this.slides.length;
    const next = ((index % count) + count) % count;
    const slide = this.slides[next];
    const trackBox = this.track.getBoundingClientRect();
    const slideBox = slide.getBoundingClientRect();
    this.track.scrollBy({ left: slideBox.left - trackBox.left, behavior: 'smooth' });
    this.setActive(next);
  }

  onScroll = () => {
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => {
      const trackBox = this.track.getBoundingClientRect();
      const center = trackBox.left + trackBox.width / 2;
      let closest = 0;
      let closestDistance = Infinity;
      this.slides.forEach((slide, i) => {
        const box = slide.getBoundingClientRect();
        const distance = Math.abs(box.left + box.width / 2 - center);
        if (distance < closestDistance) {
          closestDistance = distance;
          closest = i;
        }
      });
      this.setActive(closest);
    });
  };

  setActive(index) {
    if (index === this.index && this.thumbs[index]?.getAttribute('aria-current') === 'true') return;
    this.index = index;
    this.thumbs.forEach((thumb, i) => {
      if (i === index) {
        thumb.setAttribute('aria-current', 'true');
        thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      } else {
        thumb.removeAttribute('aria-current');
      }
    });
    if (this.counter) this.counter.textContent = `${index + 1} / ${this.slides.length}`;
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', ProductGalleryElement);
}
